import React, { useState, useEffect, useRef } from 'react';
import { X, DollarSign, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { api } from '../api';
import { colors, fontFamily } from '../theme';
import { formatBetSelection } from '../utils/betFormatting';

/**
 * CashoutModal Component
 *
 * Streams a live-updating cashout offer over SSE for any pending wager
 * (single, parlay, live-single, live-parlay) and lets the user accept it.
 * The displayed value tracks live odds in real time — there is no fixed
 * expiry window; the offer is simply "whatever it currently is."
 */
const CashoutModal = ({ betType, wager, onClose, onCashoutSuccess }) => {
  const [offer, setOffer] = useState(null);
  const [connecting, setConnecting] = useState(true);
  const [error, setError] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [success, setSuccess] = useState(false);
  const esRef = useRef(null);
  // Tracks whether the stream has ever delivered a 'cashout' or 'closed' event.
  // Lets onerror tell "first-connection failure, nothing has arrived yet" (needs a
  // user-facing error so the body isn't left blank) apart from a transient blip in an
  // otherwise-working stream (EventSource auto-retries those on its own).
  const receivedDataRef = useRef(false);
  // Mirrors whether a good offer is currently shown. The SSE listeners are bound
  // once per stream, so reading `offer` state inside them would see a stale closure.
  const hasOfferRef = useRef(false);

  useEffect(() => {
    receivedDataRef.current = false;
    hasOfferRef.current = false;
    const url = api.getCashoutStreamUrl(betType, wager.id);
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    es.addEventListener('cashout', (e) => {
      setConnecting(false);
      receivedDataRef.current = true;
      try {
        const data = JSON.parse(e.data);
        if (data.available) {
          hasOfferRef.current = true;
          setOffer(data);
          setError(null);
        } else {
          hasOfferRef.current = false;
          setOffer(null);
          setError(data.reason || 'Cashout not available');
        }
      } catch {
        // Malformed event — ignore this tick and keep showing the last good value.
        // But if no offer has landed yet there IS no last good value: without an
        // error here the modal would sit on a permanently blank body (receivedDataRef
        // is already true, so onerror won't rescue it either). Keep any existing
        // reason rather than overwriting it.
        if (!hasOfferRef.current) {
          setError((prev) => prev || 'Unable to load the cashout offer — please try again.');
        }
      }
    });

    es.addEventListener('closed', (e) => {
      setConnecting(false);
      receivedDataRef.current = true;
      try {
        const data = JSON.parse(e.data);
        setError(data.reason || 'Cashout is no longer available');
      } catch {
        setError('Cashout is no longer available');
      }
      hasOfferRef.current = false;
      setOffer(null);
      es.close();
    });

    es.onerror = () => {
      setConnecting(false);
      if (!receivedDataRef.current) {
        // Terminal failure of the very first connection attempt (e.g. session expired,
        // CORS/network failure) — nothing has arrived yet, so without this the modal
        // would render a permanently blank body (no spinner, no error, no retry).
        setError('Unable to connect — please try again.');
      }
      // Otherwise: EventSource auto-retries on its own; just note we're between updates.
    };

    return () => {
      es.close();
      esRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betType, wager.id]);

  // A rejected cashout (e.g. the value moved) comes back from the backend as an
  // HTTP 400 carrying {success:false, error, new_offer?}. axios throws on a 400,
  // so both the resolved and the thrown path route through here.
  const handleRejection = (result) => {
    if (result?.new_offer) {
      const next = result.new_offer.available ? result.new_offer : null;
      hasOfferRef.current = !!next;
      setOffer(next);
      setError('That value just changed — here\'s the current offer.');
    } else {
      setError(result?.error || 'Cashout failed');
    }
  };

  const handleCashout = async () => {
    if (!offer) return;
    setExecuting(true);
    setError(null);
    try {
      const result = await api.executeCashout(betType, wager.id, offer.cashout_amount);
      if (result.success) {
        esRef.current?.close();
        setSuccess(true);
        setTimeout(() => {
          onCashoutSuccess?.(result);
          onClose();
        }, 2000);
      } else {
        handleRejection(result);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data && (data.new_offer || data.error)) {
        handleRejection(data);
      } else {
        setError(err.message || 'Failed to execute cashout');
      }
    } finally {
      setExecuting(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const legs = wager.legs || (wager.description ? [wager] : []);
  const title = betType.includes('parlay') ? `${wager.num_legs || legs.length}-Leg Parlay` : 'Cash Out';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" style={{ background: 'rgba(20,5,25,0.97)', boxShadow: colors.shadowRaised, color: colors.textPrimary, fontFamily }}>
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'linear-gradient(to right, rgba(99,41,101,0.3), rgba(50,20,30,0.4))' }}>
          <div className="flex items-center gap-2">
            <DollarSign size={24} style={{ color: colors.odds }} />
            <h2 className="text-xl font-bold" style={{ color: colors.textPrimary }}>Cash Out</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-full transition" style={{ color: colors.textMuted }}>
            <X size={24} />
          </button>
        </div>

        <div className="p-4">
          {connecting && !offer && !error && (
            <div className="flex flex-col items-center justify-center py-8">
              <RefreshCw size={40} className="animate-spin mb-4" style={{ color: colors.gold }} />
              <p style={{ color: colors.textMuted }}>Calculating cashout offer...</p>
            </div>
          )}

          {error && !success && (
            <div className="flex flex-col items-center py-6">
              <div className="p-3 rounded-full mb-4" style={{ background: 'rgba(239,68,68,0.15)' }}>
                <AlertTriangle size={32} style={{ color: colors.negative }} />
              </div>
              <p className="text-center" style={{ color: colors.negative }}>{error}</p>
            </div>
          )}

          {success && (
            <div className="flex flex-col items-center py-8">
              <div className="p-4 rounded-full mb-4 animate-bounce" style={{ background: 'rgba(74,222,128,0.15)' }}>
                <CheckCircle size={48} style={{ color: colors.odds }} />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: colors.odds }}>Cashed Out!</h3>
              <p className="text-center" style={{ color: colors.textMuted }}>
                {formatCurrency(offer?.cashout_amount)} has been added to your balance.
              </p>
            </div>
          )}

          {offer && !success && (
            <>
              <div className="text-center py-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="text-sm mb-1" style={{ color: colors.textMuted }}>Cash Out For</p>
                <p className="text-4xl font-bold transition-all" style={{ color: colors.odds }}>
                  {formatCurrency(offer.cashout_amount)}
                </p>
                <p className="text-xs mt-2" style={{ color: colors.textFaint }}>Updates live with the odds</p>
              </div>

              <div className="py-4 space-y-3">
                <h4 className="font-semibold" style={{ color: colors.textPrimary }}>Offer Details</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-2 rounded" style={{ background: colors.sectionBg }}>
                    <p style={{ color: colors.textMuted }}>Original Bet</p>
                    <p className="font-bold" style={{ color: colors.textPrimary }}>{formatCurrency(offer.original_stake)}</p>
                  </div>
                  <div className="p-2 rounded" style={{ background: colors.sectionBg }}>
                    <p style={{ color: colors.textMuted }}>Potential Payout</p>
                    <p className="font-bold" style={{ color: colors.textPrimary }}>{formatCurrency(offer.original_potential_payout)}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <button
                  onClick={onClose}
                  disabled={executing}
                  className="flex-1 py-3 px-4 rounded-lg font-medium disabled:opacity-50 transition"
                  style={{ background: colors.sectionBg, color: colors.textPrimary }}
                >
                  Decline
                </button>
                <button
                  onClick={handleCashout}
                  disabled={executing}
                  className="flex-1 py-3 px-4 rounded-lg font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition"
                  style={{ background: colors.odds, color: '#0a040b' }}
                >
                  {executing ? (
                    <>
                      <RefreshCw size={18} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <DollarSign size={18} />
                      Cash Out {formatCurrency(offer.cashout_amount)}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashoutModal;
